import { computed } from 'vue'
import { useStore } from '../store/index'
import { useUtils } from '#imports'

/**
 * Datos que el formulario de contacto guiado deja (sessionStorage, 1 hora) para empezar el encargo online con el trámite
 * y los datos de contacto ya puestos: /encargo-online?tramite=X&precarga=1. Ref: https://trello.com/c/63NGWLHt
 */
export const PRECARGA_ENCARGO = 'encargo-precarga'
export const CADUCIDAD_PRECARGA_MS = 60 * 60 * 1000
export interface PrecargaEncargo { tramite: string; nombre: string; email: string; telefono: string; presupuesto: boolean; fecha: number }

/** Lugares de la web desde los que se puede abrir el encargo online. */
export type LugarAcceso = 'menu' | 'pie' | 'servicios' | 'equipo'

/** Páginas del propio encargo (y sus nombres antiguos): ahí los avisos para ir al encargo sobran. */
export const PAGINAS_ENCARGO = /\/(encargo-online|encargo-qr|mi-encargo|solicitud|solicitud-qr|mi-solicitud|tramite-online|tramite-qr|mi-tramite)(\/|$)/i

/**
 * Accesos al encargo online desde el resto de la web (menú, pie, servicios…).
 * Se muestran solo si el encargo online está activo y la notaría no los ha desactivado en el panel.
 */
export function useAccesoSolicitud() {
  const store = useStore()
  const { getLocalePath } = useUtils()

  const cfg = computed<any>(() => store.NOTARIA?.SolicitudWeb ?? null)
  const activa = computed(() => !!cfg.value?.Activa)

  function visible(lugar: LugarAcceso) {
    if (!activa.value) return false
    // Los accesos nuevos están apagados salvo que la notaría los active
    if (lugar === 'equipo') return cfg.value?.MostrarEnEquipo === true
    const campo = { menu: 'MostrarEnMenu', pie: 'MostrarEnPie', servicios: 'MostrarEnServicios' }[lugar]
    return cfg.value?.[campo] !== false
  }

  /** Botón flotante en móvil (apagado salvo que la notaría lo active). */
  const flotante = computed(() => activa.value && cfg.value?.BotonFlotanteMovil === true)
  const textoFlotante = computed(() => String(cfg.value?.BotonFlotanteTexto || '').trim())

  /** minotaria.es es una web agregadora: no tiene notaría propia. */
  const esAgregador = computed(() => String(store.NOTARIA?.Web?.Plantilla || '').toLowerCase() === 'minotaria'
    || String(store.NOTARIA?.Web?.Tipo || '').toLowerCase() === 'agregador')

  /** Páginas informativas de cada trámite (/tramites): en minotaria.es siempre; en una notaría, si las activa en el panel. */
  const paginasTramite = computed(() => activa.value && (esAgregador.value || cfg.value?.PaginasTramites === true))

  /** Aviso en la barra superior de contacto (apagado salvo que la notaría lo active). Ref: https://trello.com/c/H8zH4fAK */
  const avisoBarra = computed(() => activa.value && cfg.value?.AvisoBarraSuperior === true)
  const textoAvisoBarra = computed(() => String(cfg.value?.AvisoBarraTexto || '').trim())

  /** Nombre del apartado en el menú y el pie, tal cual lo escribe la notaría; vacío = "Encargo online" traducido. Ref: https://trello.com/c/vFuYjtpK */
  const textoMenu = computed(() => String(cfg.value?.TextoMenu || '').trim())

  /** En el menú principal va como botón destacado (a la derecha) en vez de como enlace normal. */
  const menuDestacado = computed(() => cfg.value?.EstiloMenu === 'BOTON')

  /** Enlace al encargo online en el idioma actual, con el canal y, si se indica, el encargo ya elegido. */
  function enlace(origen: string, tramite?: string | null) {
    const query: Record<string, string> = { origen }
    if (tramite) query.tramite = tramite
    return { path: getLocalePath('/encargo-online'), query }
  }

  /** Trámite asociado en el panel a un servicio de la web (o null: formulario general). */
  function tramiteDeServicio(servicio: any): string | null {
    const id = servicio?.Id ?? servicio?.IdValue ?? servicio?.id
    if (id === undefined || id === null) return null
    return cfg.value?.ServiciosTramite?.[String(id)] || null
  }

  return { activa, visible, menuDestacado, textoMenu, flotante, textoFlotante, esAgregador, paginasTramite, avisoBarra, textoAvisoBarra, enlace, tramiteDeServicio }
}
